import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from 'modules/auth/context';
import type { IEntity } from 'modules/auth/types';
import { saveGoal } from './api';
import type { GoalInput } from './types';

/**
 * Saves the learner's goal, then refreshes everything that depends on it: the
 * plan, today's test, and the signed-in user (so the Home card switches on for
 * a learner who had no goal). Logout and a new login replace the profile query,
 * so a save that finishes after either is not applied to the wrong session.
 */
export function useSaveGoal() {
  const { state, methods } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: GoalInput) => {
      if (!state.user) throw new Error('Authentication required');
      const userId = state.user.id;
      const profileQuery = queryClient.getQueryCache().find({ queryKey: ['auth', 'profile'], exact: true });
      const { learnerProfile } = await saveGoal(input);
      return { userId, learnerProfile, profileQuery };
    },
    onSuccess: ({ userId, learnerProfile, profileQuery }) => {
      const isCurrentSession = () =>
        profileQuery && queryClient.getQueryCache().find({ queryKey: ['auth', 'profile'], exact: true }) === profileQuery;
      if (!isCurrentSession()) return;

      methods.setUser((user) => (isCurrentSession() && user?.id === userId ? { ...user, learnerProfile } : user));
      queryClient.setQueryData<{ user: IEntity.User }>(['auth', 'profile'], (profile) =>
        profile?.user.id === userId ? { ...profile, user: { ...profile.user, learnerProfile } } : profile,
      );
      void queryClient.invalidateQueries({ queryKey: ['study-plan'] });
    },
  });
}
